/**
 * NAKED Price Testing — theme client.
 *
 * Loaded on every page. Responsibilities:
 *   1. Assign a persistent visitor UUID (cookie, 90 days, SameSite=Lax).
 *   2. Fetch active tests from the App Proxy (/apps/pricing/config). Cached 5 min.
 *   3. Assign a bucket client-side per test; rewrite visible prices in the DOM.
 *   4. Inject attributes._pricing_test_assignments on /cart/add, /cart/update,
 *      /cart/change (fetch + XHR) so the Cart Transform Function reads it.
 *   5. Rewrite prices again on AJAX-injected DOM via MutationObserver.
 *   6. Shim Meta Pixel, Klaviyo and GA4 so analytics see the test price.
 *   7. POST exposure to the App Proxy (/apps/pricing/events) once per test.
 *
 * Config shape returned by the proxy:
 *   {
 *     market: "fr",
 *     tests: [{
 *       testId: "uuid",
 *       trafficPercentage: 100,
 *       audienceFilters: {...},
 *       buckets: [{
 *         id: "uuid", weight: 50, isControl: true,
 *         prices: [{ variantId: "gid://...", priceCents: 6090, compareAtPriceCents: 14990 }]
 *       }]
 *     }]
 *   }
 *
 * Hash algorithm MUST match the server (src/lib/pricing-core/assignment.ts):
 *   bucket = first 8 hex chars of SHA-256(`${testId}:${visitorUuid}`) as uint32,
 *   then (value % totalWeight) → weighted pick in bucket order.
 */
(function () {
  'use strict';

  const PROXY_CONFIG_URL = '/apps/pricing/config';
  const PROXY_EVENTS_URL = '/apps/pricing/events';
  const COOKIE_NAME = 'naked_pt';
  const COOKIE_DAYS = 90;
  const SESSION_CACHE_KEY = 'naked_pricing_config_v1';
  const SESSION_CACHE_TTL_MS = 5 * 60 * 1000;
  const EXPOSURE_SENT_KEY = 'naked_pricing_exposed_v1';

  // ─── Utilities ──────────────────────────────────────────────────────────
  function getCookie(name) {
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (let i = 0; i < parts.length; i++) {
      const eq = parts[i].indexOf('=');
      if (eq < 0) continue;
      if (parts[i].slice(0, eq) === name) {
        try { return decodeURIComponent(parts[i].slice(eq + 1)); } catch (_) { return parts[i].slice(eq + 1); }
      }
    }
    return null;
  }

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie =
      encodeURIComponent(name) + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() +
      '; path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
  }

  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // RFC-4122 v4 fallback
    const b = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(b);
    } else {
      for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    }
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => ('0' + x.toString(16)).slice(-2));
    return h[0]+h[1]+h[2]+h[3] + '-' + h[4]+h[5] + '-' + h[6]+h[7] + '-' + h[8]+h[9] + '-' + h[10]+h[11]+h[12]+h[13]+h[14]+h[15];
  }

  function getOrCreateVisitorUuid() {
    let id = getCookie(COOKIE_NAME);
    if (!id) {
      id = uuid();
      setCookie(COOKIE_NAME, id, COOKIE_DAYS);
    }
    return id;
  }

  function detectMarket() {
    const nk = window.NakedPricing || {};
    if (nk.market && typeof nk.market === 'string') return nk.market.toLowerCase();
    if (window.Shopify && window.Shopify.country) return String(window.Shopify.country).toLowerCase();
    const m = location.pathname.match(/^\/([a-z]{2})(?:-[a-z]{2})?(?:\/|$)/i);
    if (m) return m[1].toLowerCase();
    return 'fr';
  }

  function detectDevice() {
    const w = window.innerWidth || document.documentElement.clientWidth || 0;
    if (w <= 699) return 'mobile';
    if (w <= 999) return 'tablet';
    return 'desktop';
  }

  function getUtm() {
    const sp = new URLSearchParams(location.search);
    return {
      utmSource: sp.get('utm_source') || undefined,
      utmMedium: sp.get('utm_medium') || undefined,
      utmCampaign: sp.get('utm_campaign') || undefined,
    };
  }

  // Stable, low-quality fallback hash in case SubtleCrypto is missing (old Safari).
  function fnvHashToUint32(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h >>> 0;
  }

  async function sha256FirstUint32(message) {
    if (window.crypto && window.crypto.subtle && typeof window.crypto.subtle.digest === 'function') {
      try {
        const enc = new TextEncoder().encode(message);
        const buf = await window.crypto.subtle.digest('SHA-256', enc);
        const view = new DataView(buf);
        return view.getUint32(0, false);
      } catch (_) {
        // fall through to fnv
      }
    }
    return fnvHashToUint32(message);
  }

  // ─── Assignment (mirrors pricing-core/assignment.ts) ────────────────────
  async function assignBucket(testId, visitorUuid, buckets) {
    if (!buckets || buckets.length === 0) return null;
    const slot = await sha256FirstUint32(testId + ':' + visitorUuid);
    const totalWeight = buckets.reduce((s, b) => s + (b.weight > 0 ? b.weight : 0), 0);
    if (totalWeight <= 0) return null;
    const position = slot % totalWeight;
    let cursor = 0;
    for (let i = 0; i < buckets.length; i++) {
      cursor += buckets[i].weight;
      if (position < cursor) return buckets[i];
    }
    return buckets[buckets.length - 1];
  }

  async function isVisitorInTrafficShare(testId, visitorUuid, trafficPct) {
    if (trafficPct >= 100) return true;
    if (trafficPct <= 0) return false;
    const slot = await sha256FirstUint32('traffic:' + testId + ':' + visitorUuid);
    return (slot % 100) < trafficPct;
  }

  function matchesAudience(test, ctx) {
    const a = test.audienceFilters;
    if (!a) return true;
    if (Array.isArray(a.devices) && a.devices.length && a.devices.indexOf(ctx.device) < 0) return false;
    if (Array.isArray(a.countries) && a.countries.length) {
      const m = ctx.market.toUpperCase();
      if (a.countries.map((c) => String(c).toUpperCase()).indexOf(m) < 0) return false;
    }
    if (Array.isArray(a.utmSources) && a.utmSources.length && (!ctx.utm.utmSource || a.utmSources.indexOf(ctx.utm.utmSource) < 0)) return false;
    if (Array.isArray(a.utmMediums) && a.utmMediums.length && (!ctx.utm.utmMedium || a.utmMediums.indexOf(ctx.utm.utmMedium) < 0)) return false;
    if (Array.isArray(a.utmCampaigns) && a.utmCampaigns.length && (!ctx.utm.utmCampaign || a.utmCampaigns.indexOf(ctx.utm.utmCampaign) < 0)) return false;
    return true;
  }

  // Force-assignment overrides for QA / preview links.
  // URL: `?naked_pt_force=<testId>:<bucketId>` (comma-separated for multiple).
  // Persisted in sessionStorage so internal nav keeps the override.
  function getForcedAssignments() {
    /** @type {Record<string,string>} */
    const out = {};
    try {
      const sp = new URLSearchParams(window.location.search);
      const fromUrl = sp.get('naked_pt_force');
      if (fromUrl) {
        fromUrl.split(',').forEach((pair) => {
          const [testId, bucketId] = pair.split(':');
          if (testId && bucketId) out[testId.trim()] = bucketId.trim();
        });
        try { window.sessionStorage.setItem('naked_pt_force', JSON.stringify(out)); } catch (_e) {}
        return out;
      }
      const stored = window.sessionStorage && window.sessionStorage.getItem('naked_pt_force');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') return parsed;
      }
    } catch (_e) {}
    return out;
  }

  async function assignAllTests(tests, visitorUuid, ctx) {
    /** @type {Array<{testId:string, bucketId:string, isControl:boolean, prices:Record<string,{priceCents:number, compareAtPriceCents?:number|null}>}>} */
    const out = [];
    const forced = getForcedAssignments();
    for (let i = 0; i < tests.length; i++) {
      const t = tests[i];
      if (!t || !Array.isArray(t.buckets) || t.buckets.length === 0) continue;
      const forcedBucketId = forced[t.testId];
      let bucket = null;
      if (forcedBucketId) {
        bucket = t.buckets.find((b) => b && b.id === forcedBucketId) || null;
      }
      if (!bucket) {
        if (!matchesAudience(t, ctx)) continue;
        const eligible = await isVisitorInTrafficShare(t.testId, visitorUuid, t.trafficPercentage == null ? 100 : t.trafficPercentage);
        if (!eligible) continue;
        bucket = await assignBucket(t.testId, visitorUuid, t.buckets);
      }
      if (!bucket) continue;
      const byVariant = {};
      if (Array.isArray(bucket.prices)) {
        for (let j = 0; j < bucket.prices.length; j++) {
          const p = bucket.prices[j];
          if (!p || !p.variantId) continue;
          byVariant[String(p.variantId)] = {
            priceCents: p.priceCents,
            compareAtPriceCents: p.compareAtPriceCents == null ? null : p.compareAtPriceCents,
          };
        }
      }
      out.push({ testId: t.testId, bucketId: bucket.id, isControl: !!bucket.isControl, prices: byVariant });
    }
    return out;
  }

  // ─── Config fetch + cache ───────────────────────────────────────────────
  async function loadConfig(market) {
    try {
      const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
      if (raw) {
        const entry = JSON.parse(raw);
        if (entry && entry.market === market && entry.cachedAt && (Date.now() - entry.cachedAt) < SESSION_CACHE_TTL_MS) {
          return entry.data;
        }
      }
    } catch (_) { /* ignore */ }

    try {
      const r = await fetch(PROXY_CONFIG_URL + '?market=' + encodeURIComponent(market), {
        credentials: 'same-origin',
        headers: { 'accept': 'application/json' },
      });
      if (!r.ok) return null;
      const data = await r.json();
      try {
        sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({ market: market, cachedAt: Date.now(), data: data }));
      } catch (_) { /* ignore */ }
      return data;
    } catch (_) {
      return null;
    }
  }

  // ─── Price formatting ───────────────────────────────────────────────────
  const CURRENCY_BY_MARKET = { fr: 'EUR', de: 'EUR', nl: 'EUR', es: 'EUR', it: 'EUR', be: 'EUR', uk: 'GBP', gb: 'GBP', us: 'USD', ch: 'CHF' };
  const LOCALE_BY_MARKET = { fr: 'fr-FR', de: 'de-DE', nl: 'nl-NL', es: 'es-ES', it: 'it-IT', be: 'fr-BE', uk: 'en-GB', gb: 'en-GB', us: 'en-US', ch: 'fr-CH' };

  function getFormatter(market) {
    const nk = window.NakedPricing || {};
    const currency = (nk.currency && typeof nk.currency === 'string') ? nk.currency.toUpperCase() : (CURRENCY_BY_MARKET[market] || 'EUR');
    const locale = LOCALE_BY_MARKET[market] || 'fr-FR';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency, currencyDisplay: 'symbol' });
    } catch (_) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
    }
  }

  function formatMoney(fmt, cents) {
    return fmt.format((cents || 0) / 100);
  }

  // Extract a plain variant id (numeric string) from a GID or raw value.
  function variantIdFromGid(gid) {
    if (!gid) return null;
    const s = String(gid);
    const m = s.match(/(\d+)$/);
    return m ? m[1] : s;
  }

  // Build the lookup by both GID and plain numeric id for speedy matching.
  function buildPriceLookup(assignments) {
    const byId = {};
    for (let i = 0; i < assignments.length; i++) {
      const a = assignments[i];
      const keys = Object.keys(a.prices);
      for (let j = 0; j < keys.length; j++) {
        const gid = keys[j];
        const plain = variantIdFromGid(gid);
        byId[gid] = a.prices[gid];
        if (plain) byId[plain] = a.prices[gid];
      }
    }
    return byId;
  }

  // ─── DOM rewriting ──────────────────────────────────────────────────────
  function findPriceNodes(root) {
    const nodes = [];
    const selectors = ['.price-item', '.price', '[data-price]', 'sale-price', 'compare-at-price'];
    for (let i = 0; i < selectors.length; i++) {
      const list = root.querySelectorAll(selectors[i]);
      for (let j = 0; j < list.length; j++) nodes.push(list[j]);
    }
    return nodes;
  }

  // Floor to nearest 5 — always under-promises the discount (never "claim"
  // 45% off when the real math gives 42.89%).
  function roundToFive(n) {
    return Math.floor(n / 5) * 5;
  }

  function rewriteOnSaleBadge(card, entry) {
    if (!entry.compareAtPriceCents || entry.compareAtPriceCents <= entry.priceCents) return;
    const badges = card.querySelectorAll('on-sale-badge');
    if (!badges.length) return;
    const rawPct = ((entry.compareAtPriceCents - entry.priceCents) * 100) / entry.compareAtPriceCents;
    const pctRounded = roundToFive(rawPct);
    for (let i = 0; i < badges.length; i++) {
      const b = badges[i];
      const mode = b.getAttribute('discount-mode');
      if (mode === 'percentage' || !mode) {
        b.textContent = '-' + pctRounded + '%';
      }
      b.removeAttribute('hidden');
    }
  }

  function rewriteCardPrices(scope, lookup, fmt) {
    const cards = scope.querySelectorAll('[data-variant-id]');
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const raw = card.getAttribute('data-variant-id');
      const entry = lookup[raw] || lookup[variantIdFromGid(raw) || ''];
      if (!entry) continue;
      if (card.getAttribute('data-naked-pt-applied') === '1') continue;

      const priceNodes = findPriceNodes(card);
      for (let k = 0; k < priceNodes.length; k++) {
        const el = priceNodes[k];
        if (el.tagName && el.tagName.toLowerCase() === 'compare-at-price') {
          if (entry.compareAtPriceCents && entry.compareAtPriceCents > entry.priceCents) {
            const sr = el.querySelector('.sr-only');
            const srHtml = sr ? sr.outerHTML : '';
            el.innerHTML = srHtml + formatMoney(fmt, entry.compareAtPriceCents);
            el.removeAttribute('hidden');
          }
        } else if (el.tagName && el.tagName.toLowerCase() === 'sale-price') {
          const sr = el.querySelector('.sr-only');
          const srHtml = sr ? sr.outerHTML : '';
          el.innerHTML = srHtml + formatMoney(fmt, entry.priceCents);
        } else {
          el.textContent = formatMoney(fmt, entry.priceCents);
        }
      }

      // Percentage badge (product page / card) — recompute from bucket prices.
      rewriteOnSaleBadge(card, entry);

      card.setAttribute('data-naked-pt-applied', '1');
      card.setAttribute('data-naked-pt-price-cents', String(entry.priceCents));
    }
  }

  function rewritePricesOnPage(assignments, market) {
    const lookup = buildPriceLookup(assignments);
    const fmt = getFormatter(market);
    rewriteCardPrices(document, lookup, fmt);
    return { lookup: lookup, fmt: fmt };
  }

  function installMutationObserver(state) {
    if (!window.MutationObserver) return;
    const obs = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        for (let j = 0; j < m.addedNodes.length; j++) {
          const n = m.addedNodes[j];
          if (n.nodeType !== 1) continue;
          // Scope can itself be a card OR contain cards.
          if (n.hasAttribute && n.hasAttribute('data-variant-id')) {
            rewriteCardPrices(n.parentElement || document, state.lookup, state.fmt);
          } else if (n.querySelectorAll) {
            rewriteCardPrices(n, state.lookup, state.fmt);
          }
        }
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  // ─── Cart request hook ──────────────────────────────────────────────────
  const CART_URL_RE = /\/cart\/(add|update|change)(\.js)?(\?|$)/i;

  function assignmentsForCart(assignments) {
    // Minimal payload used downstream (Cart Transform Function + server).
    return assignments.map((a) => ({ testId: a.testId, bucketId: a.bucketId, isControl: a.isControl }));
  }

  function mergeIntoJsonBody(bodyText, attrsJson) {
    try {
      const obj = JSON.parse(bodyText);
      if (!obj || typeof obj !== 'object') return bodyText;
      if (!obj.attributes || typeof obj.attributes !== 'object') obj.attributes = {};
      obj.attributes._pricing_test_assignments = attrsJson;
      return JSON.stringify(obj);
    } catch (_) {
      return bodyText;
    }
  }

  function mergeIntoUrlEncoded(bodyText, attrsJson) {
    const sp = new URLSearchParams(bodyText);
    sp.set('attributes[_pricing_test_assignments]', attrsJson);
    return sp.toString();
  }

  // Corner cart drawer rewrite — Corner renders its own drawer from its
  // own state; it does not re-read /cart.js each render, so a JSON hook
  // doesn't suffice. Scan its DOM (lines have id="corner-cowi-cart-item-
  // price-N" with N = 1-based line index) and update "Current price" /
  // "Original price" text from our bucket lookup.
  function formatCornerMoney(cents) {
    const sign = cents < 0 ? '-' : '';
    const abs = Math.abs(Math.trunc(cents));
    const major = Math.trunc(abs / 100);
    const minor = abs % 100;
    return `${sign}${major}.${minor < 10 ? '0' : ''}${minor} €`;
  }

  let cornerLastRun = 0;
  let cornerCartCache = null;
  let cornerCacheAt = 0;

  async function rewriteCornerDrawer(lookup) {
    if (!lookup) return;
    if (Date.now() - cornerLastRun < 250) return;
    cornerLastRun = Date.now();

    const priceEls = document.querySelectorAll('[id^="corner-cowi-cart-item-price-"]');
    if (!priceEls.length) return;

    // Cache cart state 2s to avoid hammering /cart.js on every mutation.
    let cart = cornerCartCache;
    if (!cart || Date.now() - cornerCacheAt > 2000) {
      try {
        const resp = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!resp.ok) return;
        cart = await resp.json();
        cornerCartCache = cart;
        cornerCacheAt = Date.now();
      } catch (_) { return; }
    }
    if (!cart || !Array.isArray(cart.items)) return;

    for (let i = 0; i < priceEls.length; i++) {
      const priceEl = priceEls[i];
      const m = /corner-cowi-cart-item-price-(\d+)/.exec(priceEl.id || '');
      if (!m) continue;
      const idx = parseInt(m[1], 10) - 1;
      const item = cart.items[idx];
      if (!item) continue;

      const vid = String(item.variant_id || '');
      const entry = lookup[vid] || lookup[variantIdFromGid(vid) || ''];
      if (!entry || typeof entry.priceCents !== 'number') continue;

      const qty = typeof item.quantity === 'number' ? item.quantity : 1;
      const lineCents = entry.priceCents * qty;
      const compareLineCents = entry.compareAtPriceCents
        ? entry.compareAtPriceCents * qty
        : null;

      // Skip if Shopify/BOGO has made the line free — don't hide "gratuit".
      if (item.final_line_price === 0 || item.price === 0) continue;

      const currentEl = priceEl.querySelector('p[aria-label="Current price"]');
      if (currentEl && currentEl.getAttribute('data-naked-pt') !== String(lineCents)) {
        currentEl.textContent = formatCornerMoney(lineCents);
        currentEl.setAttribute('data-naked-pt', String(lineCents));
      }
      const originalEl = priceEl.querySelector('p[aria-label="Original price"]');
      if (originalEl && compareLineCents && compareLineCents > lineCents) {
        if (originalEl.getAttribute('data-naked-pt') !== String(compareLineCents)) {
          originalEl.textContent = formatCornerMoney(compareLineCents);
          originalEl.setAttribute('data-naked-pt', String(compareLineCents));
        }
      }
    }
  }

  function installCornerObserver(getLookup) {
    if (!window.MutationObserver) return;
    const run = () => {
      const lookup = getLookup();
      if (lookup) rewriteCornerDrawer(lookup);
    };
    const obs = new MutationObserver(() => run());
    obs.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
    // Kick once now + periodic safety net (drawer may open without a mutation
    // we detect, and Corner re-renders its own nodes on qty change).
    run();
    setInterval(run, 1000);
  }

  // Rewrite /cart.js (and similar) JSON responses so the cart drawer /
  // any client-side code rendering the cart shows per-line test prices.
  // The server-side order_discount takes care of the real total at checkout;
  // this is purely cosmetic to match what the visitor saw on the product page.
  function installCartResponseRewrite(getLookup) {
    if (typeof window.fetch !== 'function') return;
    const CART_RESPONSE_RE = /\/cart(\.js|\.json|\/update\.js|\/change\.js|\/add\.js)?(\?|$)/;
    const prev = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const p = prev.call(this, input, init);
      if (!url || !CART_RESPONSE_RE.test(url)) return p;
      return p.then((resp) => {
        try {
          if (!resp || typeof resp.clone !== 'function') return resp;
          const ct = (resp.headers && resp.headers.get && resp.headers.get('content-type')) || '';
          if (ct.indexOf('application/json') < 0) return resp;
          return resp.clone().json().then((data) => {
            if (!data || typeof data !== 'object') return resp;
            const lookup = getLookup();
            if (!lookup) return resp;
            const items = Array.isArray(data.items) ? data.items : null;
            if (!items) return resp;

            let changed = false;
            let newSubtotal = 0;
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (!item || item.variant_id == null) {
                if (typeof item?.final_line_price === 'number') newSubtotal += item.final_line_price;
                continue;
              }
              const vid = String(item.variant_id);
              const entry = lookup[vid] || lookup[variantIdFromGid(vid) || ''];
              if (!entry) {
                if (typeof item.final_line_price === 'number') newSubtotal += item.final_line_price;
                continue;
              }
              const targetCents = entry.priceCents;
              if (typeof targetCents !== 'number') {
                if (typeof item.final_line_price === 'number') newSubtotal += item.final_line_price;
                continue;
              }
              const qty = typeof item.quantity === 'number' ? item.quantity : 1;
              // Only rewrite if the current price is higher — don't hide BOGO
              // free-item discounts that brought the line to 0.
              if (item.price > targetCents) {
                item.price = targetCents;
                item.final_price = targetCents;
                item.line_price = targetCents * qty;
                item.final_line_price = targetCents * qty;
                if (entry.compareAtPriceCents && entry.compareAtPriceCents > targetCents) {
                  item.original_price = entry.compareAtPriceCents;
                  item.original_line_price = entry.compareAtPriceCents * qty;
                }
                changed = true;
              }
              newSubtotal += (typeof item.final_line_price === 'number' ? item.final_line_price : item.price * qty);
            }

            if (!changed) return resp;
            // Update subtotals so the drawer's math is internally consistent.
            data.items_subtotal_price = newSubtotal;
            data.original_total_price = (typeof data.original_total_price === 'number'
              ? data.original_total_price
              : newSubtotal);
            // total_price is the real amount the customer pays — leave it to
            // Shopify (reflects order_discount + any code). Don't overwrite.

            const newHeaders = new Headers(resp.headers);
            newHeaders.set('content-type', 'application/json; charset=utf-8');
            return new Response(JSON.stringify(data), {
              status: resp.status,
              statusText: resp.statusText,
              headers: newHeaders,
            });
          }).catch(() => resp);
        } catch (_) {
          return resp;
        }
      }).catch(() => p);
    };
  }

  function installCartFetchHook(getAssignmentsJson) {
    // fetch
    if (typeof window.fetch === 'function') {
      const orig = window.fetch;
      window.fetch = function (input, init) {
        try {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          if (url && CART_URL_RE.test(url)) {
            init = init || {};
            const hdrs = new Headers(init.headers || (typeof input !== 'string' && input.headers) || {});
            const ct = (hdrs.get('content-type') || '').toLowerCase();
            const attrsJson = getAssignmentsJson();

            // Body might live on init.body OR in the Request object itself
            let body = init.body;
            if (body == null && typeof input !== 'string' && input && typeof input.text === 'function') {
              // Clone so we don't lock the stream; only for Requests.
              return input.clone().text().then((txt) => {
                const merged = mergeBody(txt, ct, attrsJson);
                const next = new Request(url, {
                  method: input.method,
                  headers: hdrs,
                  body: merged.body,
                  credentials: input.credentials,
                  cache: input.cache,
                  redirect: input.redirect,
                  referrer: input.referrer,
                  integrity: input.integrity,
                  mode: input.mode,
                });
                if (merged.contentType) hdrs.set('content-type', merged.contentType);
                return orig.call(window, next);
              }).catch(() => orig.call(window, input, init));
            }

            if (typeof body === 'string') {
              const merged = mergeBody(body, ct, attrsJson);
              init.body = merged.body;
              if (merged.contentType) hdrs.set('content-type', merged.contentType);
              init.headers = hdrs;
            } else if (body instanceof FormData) {
              body.set('attributes[_pricing_test_assignments]', attrsJson);
              init.body = body;
            } else if (body instanceof URLSearchParams) {
              body.set('attributes[_pricing_test_assignments]', attrsJson);
              init.body = body;
            } else if (body && typeof body === 'object') {
              // JS object (rare, but theme could pass it)
              init.body = JSON.stringify(Object.assign({}, body, {
                attributes: Object.assign({}, body.attributes || {}, { _pricing_test_assignments: attrsJson }),
              }));
              hdrs.set('content-type', 'application/json');
              init.headers = hdrs;
            }
          }
        } catch (_) { /* never block the request */ }
        return orig.call(window, input, init);
      };
    }

    function mergeBody(bodyText, contentType, attrsJson) {
      if (contentType.indexOf('application/json') >= 0) {
        return { body: mergeIntoJsonBody(bodyText, attrsJson), contentType: 'application/json' };
      }
      if (contentType.indexOf('x-www-form-urlencoded') >= 0 || (bodyText && bodyText.indexOf('=') > 0 && bodyText.indexOf('{') !== 0)) {
        return { body: mergeIntoUrlEncoded(bodyText, attrsJson), contentType: 'application/x-www-form-urlencoded' };
      }
      // Try JSON if the text looks like JSON
      const trimmed = bodyText.trim();
      if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
        return { body: mergeIntoJsonBody(bodyText, attrsJson), contentType: 'application/json' };
      }
      return { body: bodyText, contentType: null };
    }

    // XHR
    if (typeof XMLHttpRequest !== 'undefined' && XMLHttpRequest.prototype) {
      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;
      const origSetHeader = XMLHttpRequest.prototype.setRequestHeader;

      XMLHttpRequest.prototype.open = function (method, url) {
        this.__nakedPtUrl = url;
        return origOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
        if (name && name.toLowerCase() === 'content-type') this.__nakedPtCT = String(value || '').toLowerCase();
        return origSetHeader.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function (body) {
        try {
          const url = this.__nakedPtUrl || '';
          if (url && CART_URL_RE.test(url)) {
            const attrsJson = getAssignmentsJson();
            const ct = this.__nakedPtCT || '';
            if (typeof body === 'string') {
              const merged = mergeBody(body, ct, attrsJson);
              if (merged.contentType && !this.__nakedPtCT) {
                origSetHeader.call(this, 'Content-Type', merged.contentType);
              }
              return origSend.call(this, merged.body);
            }
            if (body instanceof FormData) {
              body.set('attributes[_pricing_test_assignments]', attrsJson);
              return origSend.call(this, body);
            }
            if (body instanceof URLSearchParams) {
              body.set('attributes[_pricing_test_assignments]', attrsJson);
              return origSend.call(this, body);
            }
          }
        } catch (_) { /* ignore */ }
        return origSend.apply(this, arguments);
      };
    }
  }

  // ─── Analytics shims ────────────────────────────────────────────────────
  function overridePriceInPayload(payload, lookup) {
    if (!payload || typeof payload !== 'object') return payload;
    const vid = payload.variantId || payload.variant_id || payload.content_ids || payload.content_id || payload.VariantID || payload.id;
    // Klaviyo ViewedProduct uses { ProductID, ItemId, Price }
    const productId = payload.ProductID || payload.product_id || payload.ItemId;
    let entry = null;
    const try1 = (k) => { if (!entry && k != null) entry = lookup[String(k)] || lookup[variantIdFromGid(k) || '']; };
    if (Array.isArray(vid)) vid.forEach(try1); else try1(vid);
    if (!entry) try1(productId);
    if (!entry) return payload;

    const next = Object.assign({}, payload);
    if (typeof next.value === 'number') next.value = entry.priceCents / 100;
    if (typeof next.Price === 'number') next.Price = entry.priceCents / 100;
    if (typeof next.price === 'number') next.price = entry.priceCents / 100;
    return next;
  }

  function overrideMetaPixel(lookup) {
    try {
      if (typeof window.fbq !== 'function') return;
      const orig = window.fbq;
      const shim = function () {
        try {
          const args = Array.prototype.slice.call(arguments);
          if (args[0] === 'track' || args[0] === 'trackCustom') {
            const evt = args[1];
            if (evt === 'ViewContent' || evt === 'AddToCart' || evt === 'Purchase' || evt === 'InitiateCheckout') {
              args[2] = overridePriceInPayload(args[2] || {}, lookup);
            }
          }
        } catch (_) { /* ignore */ }
        return orig.apply(this, arguments);
      };
      // Preserve known fields so downstream checks continue to work.
      for (const k in orig) { if (Object.prototype.hasOwnProperty.call(orig, k)) shim[k] = orig[k]; }
      window.fbq = shim;
    } catch (_) { /* ignore */ }
  }

  function overrideKlaviyo(lookup) {
    try {
      window._learnq = window._learnq || [];
      const orig = window._learnq;
      const push = orig.push.bind(orig);
      orig.push = function (args) {
        try {
          if (Array.isArray(args) && args[0] === 'track') {
            const evt = args[1];
            if (evt === 'Viewed Product' || evt === 'Added to Cart' || evt === 'Started Checkout') {
              args[2] = overridePriceInPayload(args[2] || {}, lookup);
            }
          }
        } catch (_) { /* ignore */ }
        return push(args);
      };
    } catch (_) { /* ignore */ }
  }

  // ─── GA4 + proxy exposure ───────────────────────────────────────────────
  function pushGA4Exposures(assignments, market) {
    window.dataLayer = window.dataLayer || [];
    for (let i = 0; i < assignments.length; i++) {
      const a = assignments[i];
      window.dataLayer.push({
        event: 'pricing_test_exposure',
        testId: a.testId,
        bucketId: a.bucketId,
        isControl: a.isControl,
        market: market,
      });
    }
  }

  function loadExposedSet() {
    try {
      const raw = sessionStorage.getItem(EXPOSURE_SENT_KEY);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      return (obj && typeof obj === 'object') ? obj : {};
    } catch (_) { return {}; }
  }

  function saveExposedSet(map) {
    try { sessionStorage.setItem(EXPOSURE_SENT_KEY, JSON.stringify(map)); } catch (_) { /* ignore */ }
  }

  function postExposureEvent(visitorUuid, assignments, market, ctx) {
    const sent = loadExposedSet();
    const pending = assignments.filter((a) => !sent[a.testId + ':' + a.bucketId]);
    if (pending.length === 0) return;

    const pageUrl = location.href;
    const device = ctx.device;
    const utm = ctx.utm;

    for (let i = 0; i < pending.length; i++) {
      const a = pending[i];
      const body = {
        visitorUuid: visitorUuid,
        testId: a.testId,
        bucketId: a.bucketId,
        market: market,
        pageUrl: pageUrl,
        device: device,
        utmSource: utm.utmSource,
        utmMedium: utm.utmMedium,
        utmCampaign: utm.utmCampaign,
      };
      try {
        const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
        if (navigator.sendBeacon && navigator.sendBeacon(PROXY_EVENTS_URL, blob)) {
          sent[a.testId + ':' + a.bucketId] = Date.now();
          continue;
        }
      } catch (_) { /* fall through to fetch */ }

      try {
        fetch(PROXY_EVENTS_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {});
        sent[a.testId + ':' + a.bucketId] = Date.now();
      } catch (_) { /* ignore */ }
    }
    saveExposedSet(sent);
  }

  // ─── Main ───────────────────────────────────────────────────────────────
  const visitorUuid = getOrCreateVisitorUuid();
  const market = detectMarket();
  const device = detectDevice();
  const utm = getUtm();
  const ctx = { market: market, device: device, utm: utm };

  loadConfig(market).then((config) => {
    if (!config || !Array.isArray(config.tests) || config.tests.length === 0) return;
    return assignAllTests(config.tests, visitorUuid, ctx).then((assignments) => {
      if (!assignments.length) return;

      const compact = assignmentsForCart(assignments);
      let assignmentsJson = JSON.stringify(compact);

      // Price rewrite (main DOM + then MutationObserver for later-injected).
      const state = rewritePricesOnPage(assignments, market);
      installMutationObserver(state);

      // Cart hooks — read assignments lazily so future updates can swap them.
      installCartFetchHook(() => assignmentsJson);
      // Rewrite per-line prices in /cart.js-style responses so the cart
      // drawer mirrors the product-page pricing even though the server-side
      // discount is an order-level lump sum.
      installCartResponseRewrite(() => state.lookup);
      // Corner cart drawer DOM rewrite — Corner renders from its own state,
      // this targets its markup directly.
      installCornerObserver(() => state.lookup);

      // Analytics shims
      overrideMetaPixel(state.lookup);
      overrideKlaviyo(state.lookup);
      pushGA4Exposures(assignments, market);

      // Proxy exposure (once per session per test:bucket)
      postExposureEvent(visitorUuid, assignments, market, ctx);

      // Expose for debugging
      window.NakedPricing = Object.assign({}, window.NakedPricing || {}, {
        visitorUuid: visitorUuid,
        assignments: assignments,
        assignmentsJson: assignmentsJson,
      });
    });
  }).catch(() => { /* never throw — never break the page */ });
})();
